import { collectRefsForTerm } from '@app/hcs-shared/_entity/expression/antlr/get-ids';
import { exists } from '@nu-art/ts-common';
export function getNeededParsedExpressionIds(description, valueTree, expression) {
    const result = [];
    collectNeededRefs(description, valueTree, expression, result);
    return result;
}
function collectNeededRefs(description, valueTree, expression, result) {
    if (!hasUnresolvedTerms(description, valueTree))
        return;
    switch (description.type) {
        case 'atom':
        case 'number':
            return;
        case 'term': {
            const term = expression.terms[description.termIndex - 1];
            if (term)
                collectRefsForTerm(term, result);
            return;
        }
        case 'unary': {
            const innerTree = valueTree?.type === 'unary' ? valueTree.inner : undefined;
            collectNeededRefs(description.operand, innerTree, expression, result);
            return;
        }
        case 'group': {
            const innerTree = valueTree?.type === 'group' ? valueTree.inner : undefined;
            collectNeededRefs(description.expression, innerTree, expression, result);
            return;
        }
        case 'binary': {
            const leftTree = valueTree?.type === 'binary' ? valueTree.left : undefined;
            const rightTree = valueTree?.type === 'binary' ? valueTree.right : undefined;
            if (description.operator === 'or') {
                if (isDefinitively(description.leftHand, leftTree, true))
                    return;
                if (isDefinitively(description.rightHand, rightTree, true))
                    return;
            }
            else if (description.operator === 'and') {
                if (isDefinitively(description.leftHand, leftTree, false))
                    return;
                if (isDefinitively(description.rightHand, rightTree, false))
                    return;
            }
            collectNeededRefs(description.leftHand, leftTree, expression, result);
            collectNeededRefs(description.rightHand, rightTree, expression, result);
            return;
        }
        case 'comparison': {
            const leftTree = valueTree?.type === 'comparison' ? valueTree.left : undefined;
            const rightTree = valueTree?.type === 'comparison' ? valueTree.right : undefined;
            collectNeededRefs(description.leftHand, leftTree, expression, result);
            collectNeededRefs(description.rightHand, rightTree, expression, result);
            return;
        }
        case 'variadic': {
            const operandTrees = valueTree?.type === 'variadic' ? valueTree.operands : [];
            if (description.operator === 'or') {
                if (description.operands.some((op, i) => isDefinitively(op, operandTrees[i], true)))
                    return;
            }
            else if (description.operator === 'and') {
                if (description.operands.some((op, i) => isDefinitively(op, operandTrees[i], false)))
                    return;
            }
            description.operands.forEach((op, i) => collectNeededRefs(op, operandTrees[i], expression, result));
            return;
        }
        case 'range': {
            const minTree = valueTree?.type === 'range' ? valueTree.min : undefined;
            const maxTree = valueTree?.type === 'range' ? valueTree.max : undefined;
            collectNeededRefs(description.min, minTree, expression, result);
            collectNeededRefs(description.max, maxTree, expression, result);
            return;
        }
    }
}
// A subtree is "fully resolved" iff every term leaf in it has a defined value.
// The value tree's intermediate `value` fields can't be trusted for this check —
// `Boolean(undefined)` is false, so an OR/AND with unresolved operands often
// reports `value: false` even though the result isn't actually decided yet.
function hasUnresolvedTerms(description, valueTree) {
    switch (description.type) {
        case 'atom':
        case 'number':
            return false;
        case 'term':
            return !valueTree || valueTree.type !== 'term' || !exists(valueTree.value);
        case 'unary':
            return hasUnresolvedTerms(description.operand, valueTree?.type === 'unary' ? valueTree.inner : undefined);
        case 'group':
            return hasUnresolvedTerms(description.expression, valueTree?.type === 'group' ? valueTree.inner : undefined);
        case 'binary': {
            const left = valueTree?.type === 'binary' ? valueTree.left : undefined;
            const right = valueTree?.type === 'binary' ? valueTree.right : undefined;
            return hasUnresolvedTerms(description.leftHand, left) || hasUnresolvedTerms(description.rightHand, right);
        }
        case 'comparison': {
            const left = valueTree?.type === 'comparison' ? valueTree.left : undefined;
            const right = valueTree?.type === 'comparison' ? valueTree.right : undefined;
            return hasUnresolvedTerms(description.leftHand, left) || hasUnresolvedTerms(description.rightHand, right);
        }
        case 'variadic': {
            const operands = valueTree?.type === 'variadic' ? valueTree.operands : [];
            return description.operands.some((op, i) => hasUnresolvedTerms(op, operands[i]));
        }
        case 'range': {
            const min = valueTree?.type === 'range' ? valueTree.min : undefined;
            const max = valueTree?.type === 'range' ? valueTree.max : undefined;
            return hasUnresolvedTerms(description.min, min) || hasUnresolvedTerms(description.max, max);
        }
    }
}
function isDefinitively(description, valueTree, target) {
    if (hasUnresolvedTerms(description, valueTree))
        return false;
    return Boolean(valueTree?.value) === target;
}
//# sourceMappingURL=get-needed-ids.js.map